import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ISelectItem } from 'app/store/models/select.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { MatSelectChange } from '@angular/material/select';
import { IContactGroup } from 'app/store/models/contact.model';
import { ContactsService } from 'app/store/services/contacts.service';
import { SessionService } from 'app/store/services/session.service';
import { takeUntil } from 'rxjs/operators';
import { FLASH_TYPE, IUserSessionData } from 'app/store/models/user.model';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { HttpErrorResponse } from '@angular/common/http';
import { openDialog, openSnackbar } from 'app/store/actions/page.action';
import { Store } from '@ngrx/store';
import { IReducers } from 'app/store/reducers';
import { setFlash } from 'app/store/actions/user.action';
import { DataSourceParams, ITableColumn } from '../../store/models/table.model';
import { StaticDataSource } from '../../datasources/static.datasource';
import { EventService } from '../../store/services/event.service';
import { ButtonDisablerService } from '../../store/services/button-disabler.service';

export const EVENT_UPLOAD_DELETED = 'event_uploading.deleted';

@Component({
  selector: 'app-process-upload',
  templateUrl: './process-upload.component.html',
  styleUrls: ['./process-upload.component.scss'],
})
export class ProcessUploadComponent implements OnInit, OnDestroy {
  contactGroupsItems: ISelectItem[] = [
    {
      label: 'Add new group',
      value: 'new',
    },
  ];
  fieldMaps: { [key: string]: ISelectItem[] } = {};
  formGroup: FormGroup;
  showGroupControl: boolean;
  sessionData: IUserSessionData;
  componentReady = false;
  firstRow: { [key: string]: string };
  firstRowKeys: string[];
  mapping: { [key: string]: string };
  matchedFields: { [key: string]: string };
  samples: { [key: string]: string }[];
  map: any;
  displayedColumns: any;
  tableColumns: ITableColumn<any>[] = [];
  tableDataSource: StaticDataSource<any>;
  tableDatasourceParams: DataSourceParams = {
    page: 1,
    pageSize: 10,
    sortAttr: 'Email',
    sortDir: 'desc',
    filters: {},
  };
  submitButtonProperties: Button = {
    color: ButtonColor.ORANGE,
    text: 'Submit',
    size: ButtonSize.SMALL,
    onClick: () => this.submitForm(),
  };
  submitButtonDisabled: boolean;
  importError: string;
  tableReady: boolean;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private fileId: number;
  cancelButtonProperteis: Button = {
    color: ButtonColor.WHITE,
    text: 'Cancel',
    size: ButtonSize.SMALL,
    onClick: () => this.store.dispatch(openDialog({
      dialogData: {
        content: 'Are you sure you want to cancel?',
        title: 'Cancel contacts import?',
        canClose: true,
        buttons: [
          {
            text: 'Yes',
            color: ButtonColor.RED,
            size: ButtonSize.NORMAL,
            onClick: (dialog) => this.contactService.deleteUploadedContactsFile(this.fileId)
              .subscribe(() => {
                this.store.dispatch(openSnackbar({
                  snackbarData: {
                    data: {
                      message: 'Contact upload was successfully canceled.',
                      type: 'success',
                    },
                  },
                }));
                dialog.closeDialog();
                this.router.navigate(['/user_contacts', 'user_groups']);
              }, err => {
                this.store.dispatch(openSnackbar({
                  snackbarData: {
                    data: {
                      message: err.error,
                      type: 'error',
                    },
                  },
                }));
                dialog.closeDialog();
              }),
          },
        ],
      },
    })),
  };
  private fileProcessed: boolean;

  constructor(
    private activatedRoute: ActivatedRoute,
    private contactService: ContactsService,
    private sessionService: SessionService,
    private router: Router,
    private store: Store<IReducers>,
    private readonly eventService: EventService,
    private readonly buttonDisablerService: ButtonDisablerService,
  ) {
  }

  get group_name() {
    return this.formGroup.get('group_name');
  }

  @HostListener('window:beforeunload')
  unloadHandler() {
    this.deleteFile();
  }

  submitForm(): void {
    if (Object.values(this.formGroup.value).indexOf('email') === -1) {
      this.store.dispatch(openSnackbar({
        snackbarData: {
          data: {
            message: 'You need to map at least email address',
            type: 'error',
          },
        },
      }));

      this.buttonDisablerService.disableActive();
      return;
    }
    if (this.formGroup.valid) {
      this.submitButtonDisabled = true;
      if (
        this.formGroup.get('group_id').value === 'new' &&
        this.group_name.value
      ) {
        this.contactService
          .createGroup(this.group_name.value)
          .subscribe(result => {
            if (result && result.contact_group_id) {
              this.processUpload(result.contact_group_id);
            }
            this.submitButtonDisabled = false;
          });
      } else {
        this.processUpload(+this.formGroup.get('group_id').value);
      }
    } else {
      this.formGroup.markAllAsTouched();
      window.scrollTo(0, 0);
      this.buttonDisablerService.disableActive();
    }
  }

  processUpload(groupId: number) {
    const data = this.formGroup.value;
    // get the data first before deleting the object by reference
    const keepGroup = data.keep_group;

    if (data.hasOwnProperty('group_id')) {
      delete data.group_id;
    }

    data.group_id = groupId;

    for (const key in data) {
      if (data.hasOwnProperty(key) && !data[key]) {
        delete data[key];
      }
    }

    this.contactService
      .importUserContacts(this.formGroup.value, this.fileId, groupId, keepGroup)
      .subscribe(
        () => {
          this.fileProcessed = true;
          this.store.dispatch(
            setFlash({
              data: {
                type: FLASH_TYPE.ALERT,
                alertMessage: 'Upload process successfully started!',
              },
            }),
          );
          this.router.navigate(['/user_contacts', 'add_contact']);
        },
        (error: HttpErrorResponse) => {
          this.submitButtonDisabled = false;
          this.fileProcessed = false;
          if (error && error.error && error.error.message) {
            this.importError = error.error.message;
          }
        },
      );
  }

  onGroupSelection(value: MatSelectChange) {
    this.showGroupControl = value.value === 'new';

    if (value.value === 'new') {
      this.formGroup.get('group_name').setValidators(Validators.required);
    }
  }

  checkNewGroup(value: string) {
    this.contactService
      .checkExistingGroup(value)
      .subscribe((groups: IContactGroup[]) => {
        if (groups.length === 1 && groups[0].group_name === value) {
          this.formGroup.get('group_id').setValue(groups[0].group_id);
          this.showGroupControl = false;
        }
      });
  }

  ngOnInit() {
    this.fileId = this.activatedRoute.snapshot.params.id;
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(sessionData => {
        this.sessionData = sessionData;

        this.contactService
          .getContactGroups(sessionData.id)
          .subscribe((groups: IContactGroup[]) => {
            groups.forEach((group: IContactGroup) =>
              this.contactGroupsItems.push({
                label: group.group_name,
                value: group.group_id,
              }),
            );

            this.formGroup = new FormGroup({
              group_id: new FormControl('', [Validators.required]),
              group_name: new FormControl(''),
              keep_group: new FormControl(true, Validators.required),
            });

            this.contactService
              .getUploadFileFieldsMap(this.fileId)
              .subscribe(mappings => {
                console.log(mappings);
                if (
                  mappings &&
                  mappings.samples &&
                  mappings.samples.length > 0
                ) {
                  if (mappings.map) {
                    console.log(mappings.map);
                    this.map = mappings.map;
                  }

                  const firstSample = mappings.samples[0];
                  this.firstRow = firstSample;
                  this.firstRowKeys = Object.keys(firstSample).filter(k => k.length > 0);
                  this.filterFunction(this.firstRowKeys).forEach(k => {
                    this.tableColumns.push({
                      title: k,
                      id: k,
                      sortable: true,
                    });
                  });
                  this.mapping = mappings.map;
                  this.samples = mappings.samples;
                  this.tableDataSource = new StaticDataSource(this.samples);
                  this.tableReady = true;
                  this.displayedColumns = this.firstRowKeys;
                  this.matchedFields = mappings.matched_fields;

                  for (const key in firstSample) {
                    if (firstSample.hasOwnProperty(key)) {
                      this.formGroup.addControl(
                        key,
                        new FormControl(
                          mappings.map.hasOwnProperty(key) ? key : null,
                          ['firstname', 'email'].indexOf(key) !== -1 ? [Validators.required] : [],
                        ),
                      );
                      this.preselect(key);

                      const currentMappings = [
                        {
                          label: 'Unset',
                          value: null,
                        },
                      ];
                      for (const keyMap in mappings.map) {
                        if (mappings.map.hasOwnProperty(keyMap)) {
                          currentMappings.push({
                            label: mappings.map[keyMap],
                            value: keyMap,
                          });
                        }
                      }

                      this.fieldMaps[key] = currentMappings;
                    }
                  }
                  this.displayedColumns = this.filterFunction(
                    this.displayedColumns,
                  );
                  this.displayedColumns.push('Star');
                  this.componentReady = true;
                } else {
                  this.store.dispatch(
                    openDialog({
                      dialogData: {
                        title: `Error`,
                        content: `There is something wrong with the uploaded file.`,
                        dialogMinWidth: '1000px',
                        canClose: true,
                        buttons: [
                          {
                            text: 'Ok',
                            actAsClose: true,
                            color: ButtonColor.GREEN,
                            size: ButtonSize.NORMAL,
                            onClick: () => {
                              this.router.navigate([
                                '/user_contacts',
                                'add_contact',
                              ]);
                            },
                          },
                        ],
                      },
                    }),
                  );
                }
              }, err => this.store.dispatch(openSnackbar({
                snackbarData: {
                  data: {
                    message: err.error,
                    type: 'error',
                  }
                }
              })));
          }, err => this.store.dispatch(openSnackbar({
            snackbarData: {
              data: {
                message: err.error,
                type: 'error',
              }
            }
          })));
      });
  }

  deleteFile() {
    if (this.fileProcessed) {
      return;
    }
    this.contactService.deleteUploadedContactsFile(this.fileId)
      .subscribe(() => this.eventService.broadcast(EVENT_UPLOAD_DELETED, true));
  }

  ngOnDestroy() {
    this.deleteFile();
    this.destroy$.next(false);
  }

  getCellNumber() {
    return Math.round(12 / this.firstRowKeys.length);
  }

  getSampleKeys(sample: { [key: string]: string }): string[] {
    return Object.keys(sample);
  }

  preselect(value: string) {
    const control = this.formGroup.get(`${value}`);
    if (!this.matchedFields || !Object.keys(this.matchedFields).length || !control) {
      return;
    }
    let key = Object.keys(this.matchedFields).find(
      k => k === value,
    );
    key = this.matchedFields[key];
    control.setValue(key);
    return key;
  }

  filterFunction(yourCollection): any[] {
    return yourCollection.filter(i => i !== '');
  }
}
